import apiV2 from '../api/v2';
import { putBlobToSignedUrl, type UploadTicket } from '@odo/shared-ui/files/putToSignedUrl';
import type {
    RDIFormData,
    SDIFormData,
    AlignerFieldToggles,
    ManufacturerPdfRef
} from '@odo/shared-ui/clinical/types';
import type { TimelineEvent } from '../components/patient-files/actions/types';
import type { DxReportPayload } from '../components/patient-files/actions/dx-report/types';
import type { TiptapDoc } from '@odo/shared-ui';
import type { VirtualAppointmentDraftPlan } from './virtualAppointments';

export interface SdiDraftBody {
    formData: Partial<SDIFormData>;
    virtualAppointments?: VirtualAppointmentDraftPlan[];
    virtualAppointmentsSeenIds?: string[];
    elasticsEnabled: boolean;
    alignerScheduleEnabled: boolean;
    alignerFields?: AlignerFieldToggles;
    groupId?: string;
    imageFileIds?: string[];
    manufacturerPdf?: ManufacturerPdfRef | null;
}

export interface RdiDraftBody {
    formData: Partial<RDIFormData>;
    virtualAppointments?: VirtualAppointmentDraftPlan[];
    virtualAppointmentsSeenIds?: string[];
    elasticsEnabled: boolean;
    alignerScheduleEnabled: boolean;
    alignerFields?: AlignerFieldToggles;
    groupId?: string;
    imageFileIds?: string[];
    notesDoc?: TiptapDoc;
    manufacturerPdf?: ManufacturerPdfRef | null;
}

async function requestPdfUploadTicket(
    url: string,
    filename: string,
    sizeBytes: number
): Promise<UploadTicket> {
    const response = await apiV2.post<{ success: boolean; data: UploadTicket }>(url, {
        filename,
        sizeBytes
    });
    return response.data.data;
}

export interface UploadManufacturerAttachmentOptions {
    onProgress?: (fraction: number) => void;
    onRegisterAbort?: (abort: () => void) => void;
}

// GAE caps requests at ~32MB, so the PDF goes straight to GCS via a signed URL,
// then confirm flips the File row to READY and links the PatientFile.
export async function uploadManufacturerAttachment(
    patientId: string,
    kind: 'sdi' | 'rdi',
    file: File,
    opts: UploadManufacturerAttachmentOptions = {}
): Promise<ManufacturerPdfRef> {
    const ticket = await requestPdfUploadTicket(
        `/patients/${patientId}/clinical/${kind}/attachments/upload-url`,
        file.name,
        file.size
    );

    await putBlobToSignedUrl(ticket, file, {
        onProgress: opts.onProgress,
        onRegisterAbort: opts.onRegisterAbort
    });

    const confirmResponse = await apiV2.post<{ success: boolean; data: ManufacturerPdfRef }>(
        `/patients/${patientId}/clinical/${kind}/attachments/${ticket.fileId}/confirm`
    );
    return confirmResponse.data.data;
}

// Returns the fileId for the intake request. A failed PUT abandons the ticket, so
// the intake never runs and no orphan finalized file is left behind.
export async function uploadFinalPdf(
    ticketUrl: string,
    blob: Blob,
    filename: string
): Promise<string> {
    const ticket = await requestPdfUploadTicket(ticketUrl, filename, blob.size);
    await putBlobToSignedUrl(ticket, blob);
    return ticket.fileId;
}

export async function fetchSdiDrafts(patientId: string): Promise<TimelineEvent[]> {
    const response = await apiV2.get<{ success: boolean; data: TimelineEvent[] }>(
        `/patients/${patientId}/clinical/sdi/drafts`
    );
    return response.data.data;
}

export async function createSdiDraft(
    patientId: string,
    body: SdiDraftBody
): Promise<TimelineEvent> {
    const response = await apiV2.post<{ success: boolean; data: TimelineEvent }>(
        `/patients/${patientId}/clinical/sdi/drafts`,
        body
    );
    return response.data.data;
}

export async function updateSdiDraft(
    patientId: string,
    eventId: string,
    body: SdiDraftBody
): Promise<TimelineEvent> {
    const response = await apiV2.put<{ success: boolean; data: TimelineEvent }>(
        `/patients/${patientId}/clinical/sdi/drafts/${eventId}`,
        body
    );
    return response.data.data;
}

export async function deleteSdiDraft(patientId: string, eventId: string): Promise<void> {
    await apiV2.delete(`/patients/${patientId}/clinical/sdi/drafts/${eventId}`);
}

export async function fetchRdiDrafts(patientId: string): Promise<TimelineEvent[]> {
    const response = await apiV2.get<{ success: boolean; data: TimelineEvent[] }>(
        `/patients/${patientId}/clinical/rdi/drafts`
    );
    return response.data.data;
}

export async function createRdiDraft(
    patientId: string,
    body: RdiDraftBody
): Promise<TimelineEvent> {
    const response = await apiV2.post<{ success: boolean; data: TimelineEvent }>(
        `/patients/${patientId}/clinical/rdi/drafts`,
        body
    );
    return response.data.data;
}

export async function updateRdiDraft(
    patientId: string,
    eventId: string,
    body: RdiDraftBody
): Promise<TimelineEvent> {
    const response = await apiV2.put<{ success: boolean; data: TimelineEvent }>(
        `/patients/${patientId}/clinical/rdi/drafts/${eventId}`,
        body
    );
    return response.data.data;
}

export async function deleteRdiDraft(patientId: string, eventId: string): Promise<void> {
    await apiV2.delete(`/patients/${patientId}/clinical/rdi/drafts/${eventId}`);
}

export interface DxReportDraftBody {
    formData: Partial<DxReportPayload>;
    groupId?: string;
    imageFileIds?: string[];
}

export async function fetchDxReportDrafts(patientId: string): Promise<TimelineEvent[]> {
    const response = await apiV2.get<{ success: boolean; data: TimelineEvent[] }>(
        `/patients/${patientId}/clinical/dx-report/drafts`
    );
    return response.data.data;
}

export async function createDxReportDraft(
    patientId: string,
    body: DxReportDraftBody
): Promise<TimelineEvent> {
    const response = await apiV2.post<{ success: boolean; data: TimelineEvent }>(
        `/patients/${patientId}/clinical/dx-report/drafts`,
        body
    );
    return response.data.data;
}

export async function updateDxReportDraft(
    patientId: string,
    eventId: string,
    body: DxReportDraftBody
): Promise<TimelineEvent> {
    const response = await apiV2.put<{ success: boolean; data: TimelineEvent }>(
        `/patients/${patientId}/clinical/dx-report/drafts/${eventId}`,
        body
    );
    return response.data.data;
}

export async function deleteDxReportDraft(patientId: string, eventId: string): Promise<void> {
    await apiV2.delete(`/patients/${patientId}/clinical/dx-report/drafts/${eventId}`);
}

export interface ApproveDxReportInput {
    patientId: string;
    draftId: string;
    formData: DxReportPayload;
    pdf: Blob;
    patientName: string;
}

export async function approveDxReportFromReview(
    input: ApproveDxReportInput
): Promise<TimelineEvent> {
    const pdfFileId = await uploadFinalPdf(
        `/patients/${input.patientId}/workflow/design/dx-report/pdf/upload-url`,
        input.pdf,
        'dx-report.pdf'
    );

    const body = {
        pdfFileId,
        formData: input.formData,
        patientName: input.patientName,
        draftId: input.draftId
    };

    const response = await apiV2.post<{ success: boolean; data: TimelineEvent }>(
        `/patients/${input.patientId}/workflow/design/dx-report/approve`,
        body
    );
    return response.data.data;
}
